import { ButtonSpinner } from "./ButtonSpinner";

/**
 * The app's one button primitive. Every action button should route through this
 * so restyling (brand colour, radius, focus ring, spacing) is a one-file change.
 *
 * - `variant` picks the colour family, `size` the padding.
 * - `loading` disables the button and overlays a centered ButtonSpinner (replaces
 *   the copy-pasted `{loadingDyn === name && <ButtonSpinner />}` + bg-swap blocks).
 * - `className` is appended for one-off layout (e.g. `ml-5`, `w-full`).
 * - All other props (`onClick`, `name`, `disabled`, `type`, …) pass through.
 */
const BASE =
  "relative whitespace-nowrap inline-flex items-center justify-center font-medium " +
  "focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-40 " +
  "disabled:pointer-events-none";

// Each size owns its full "chrome" (padding + radius + border/shadow). md/sm are
// the standard bordered+shadowed action buttons; chip is the lightweight,
// borderless toolbar/inline style.
const SIZES = {
  md: "px-4 py-2 text-sm rounded-md shadow-sm border border-transparent",
  sm: "px-2 py-1 text-sm rounded-md shadow-sm border border-transparent",
  chip: "px-3 py-1 text-sm rounded",
};

const VARIANTS = {
  primary: "text-white bg-primary-900 hover:bg-primary-700 focus:ring-primary-500",
  secondary: "text-white bg-orange-900 hover:bg-orange-700 focus:ring-orange-700",
  danger: "text-white bg-red-500 hover:bg-red-600 focus:ring-red-500",
  success: "text-white bg-green-600 hover:bg-green-700 focus:ring-green-600",
  info: "text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
  grey: "bg-gray-200 hover:bg-gray-300 hover:text-orange-900 focus:ring-gray-300",
  // Post-baseline variant, kept for its call sites (downloads, exports …) and
  // mapped to the grey the original UI used for those same secondary actions.
  tonal: "bg-gray-200 hover:bg-gray-300 hover:text-orange-900 focus:ring-gray-300",
};

export type ButtonSize = keyof typeof SIZES;
export type ButtonVariant = keyof typeof VARIANTS;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Colour family. */
  variant?: ButtonVariant;
  /** Padding/chrome preset. */
  size?: ButtonSize;
  /** When true, disables the button and overlays a centered spinner. */
  loading?: boolean;
  /** For download/generate buttons: overlays a download glyph in the spinner while loading. */
  download?: boolean;
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  download = false,
  disabled = false,
  className = "",
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      disabled={disabled || loading}
      className={`${BASE} ${SIZES[size]} ${VARIANTS[variant]} ${className}`}
      {...rest}
    >
      {children}
      {/* inherit → white spinner on the colour variants, dark on grey, so it's
          always visible without the old "turn the button white" swap. */}
      {loading && <ButtonSpinner color="inherit" download={download} />}
    </button>
  );
}
